import { Checkmark } from '@main/core-ui/src/Icon/constants/assets/checkmark';
import { createFrontendUuidv4 } from '@main/utils';
import type { KeyboardEvent } from 'react';
import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';

import {
  CheckboxInputContainer,
  CheckmarkContainer,
  HiddenCheckbox,
  LabelWrapper,
} from './wrappers';

/**
 * represents the checked state of the checkbox input
 */
export interface CheckboxInputProps
  extends Omit<
    React.ComponentPropsWithRef<'input'>,
    'children' | 'onChange' | 'type' | 'enterKeyHint' | 'name'
  > {
  /** Text for enter key on virtual keyboards */
  enterKeyHint?:
    | 'enter'
    | 'done'
    | 'go'
    | 'next'
    | 'previous'
    | 'search'
    | 'send'
    | undefined;
  /** label element */
  children: React.ReactNode;
  /** on change callback with boolean value */
  onChange?: (value: boolean) => void;
  /** message for input name */
  name?: string;
  /** value for radio button */
  value?: string;
  /** show checkmark */
  showCheckmark?: boolean;
}

export const CheckboxInput = React.forwardRef<
  HTMLInputElement,
  CheckboxInputProps
>(
  (
    {
      children,
      disabled = false,
      checked = false,
      showCheckmark = true,
      onChange,
      name,
      value,
      ...props
    },
    ref,
  ) => {
    const id = useMemo(() => createFrontendUuidv4(), []);
    const theme = useTheme();

    return (
      <CheckboxInputContainer
        onKeyDown={(event: KeyboardEvent): void => {
          if (event.key === ' ' && !disabled) {
            onChange?.(!checked);
          }
        }}
        onMouseDown={() => {
          if (!disabled) {
            onChange?.(!checked);
          }
        }}
        checkboxInputValue={checked ? 'checked' : 'unchecked'}
        disabled={disabled}
        tabIndex={0}
      >
        <HiddenCheckbox
          ref={ref}
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          name={name}
          // no-op onChange to prevent React warning
          // eslint-disable-next-line no-empty-function
          onChange={() => {}}
          tabIndex={-1}
          value={value || ''}
          {...props}
        />
        <LabelWrapper htmlFor={id}>
          {showCheckmark && (
            <CheckmarkContainer>
              {checked && <Checkmark color={theme.colors.white} size="14" />}
            </CheckmarkContainer>
          )}
          {children}
        </LabelWrapper>
      </CheckboxInputContainer>
    );
  },
);
